<template>
  <div>
    <v-row
      v-if="Array.isArray(backendFiles)"
      class="images-uploaded"
      :style="{padding: padding + 'px' }"
    >
      <v-col>
        <VFileDragAndDrop
          v-if="!displayedFiles.length"
          :file="newFilesInManager.file"
          :mimes="mimetype"
          @onChange="onChange"
          @removeFile="removeNewFile"
        />
        <div>
          <VTableLocal
            :items="displayedFiles"
            :isSortable="false"
            :disablePagination="true"
            :headers="tableHeaders"
            @iconAction="onClickAction"
          >
            <template #body="{items}">
              <draggable
                :list="items"
                :options="{disabled: false}"
                tag="tbody"
                @change="onDragChange"
              >
                <tr
                  v-for="(item,index) in items"
                  :key="index"
                >
                  <template v-for="(headerN,indexN) in tableHeaders">
                    <td
                      v-if="headerN.value === 'drag_drop'"
                      :key="`${index}_${indexN}_drag`"
                    >
                      <template>
                        <v-icon
                          large
                          class="hidden-md-and-down"
                          color="primary"
                        >
                          mdi-drag-vertical
                        </v-icon>
                        <span @click="activateTooltip(item.order)"> {{ item.order }} </span>
                      </template>
                    </td>
                    <td
                      v-else-if="headerN.value==='action'"
                      :key="`${index}_${indexN}_action`"
                    >
                      <template v-if="actions">
                        <div class="action-buttons">
                          <VTableAction
                            :itemActions="actions"
                            :verticalActionDots="true"
                            @iconAction="(action)=>onClickAction(item, action)"
                          />
                        </div>
                      </template>
                    </td>
                    <td
                      v-else-if="headerN.value==='fileName'"
                      :key="`${index}_${indexN}_others`"
                    >
                      <span>{{ item.link.fileName }}</span>
                      <div
                        v-if="isTooltipActive(item.order)"
                        class="custom-drag-input"
                      >
                        <div class="wrapper-div">
                          <v-text-field
                            v-model="customDragItemPosition"
                            @keydown.enter="onManualPositionInput(item)"
                          />
                        </div>
                      </div>
                    </td>
                    <td
                      v-else
                      :key="`${index}_${indexN}_others`"
                    >
                      {{ item[headerN.value] }}
                    </td>
                  </template>
                </tr>
              </draggable>
            </template>
          </VTableLocal>
        </div>
      </v-col>
    </v-row>
    <v-row v-else>
      <VFileDragAndDrop
        :file="newFilesInManager.file"
        :mimes="mimetype"
        @onChange="onChange"
        @removeFile="removeNewFile"
      />
    </v-row>
    <div
      v-if="isPreviewModalVisible"
      class="custom-overlay"
      @click="isPreviewModalVisible = false"
    />

    <v-dialog
      v-model="isPreviewModalVisible"
      class="custom-dialog"
    >
      <v-card>
        <v-container fluid>
          <v-row
            justify="center"
            align="center"
          >
            <v-col
              cols="12"
              sm="10"
              md="8"
              lg="6"
            >
              <div class="flex-center">
                <v-img
                  :src="previewFileUrl"
                  :style="{ maxWidth: previewImageWidth + 'px', maxHeight: previewImageHeight + 'px' }"
                  class="image-preview"
                />
              </div>
            </v-col>
          </v-row>
        </v-container>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            text
            @click="closePreview"
          >
            {{ $t('fileManager.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>

import { mapGetters, mapActions } from 'vuex';
import VFileDragAndDrop from '@/components/shared/widgets/forms/VuetifyInputs/VFileDragAndDrop';
import VTableLocal from '@/components/shared/content-views/VTableLocal.vue';
import draggable from 'vuedraggable';
import VTableAction from '@/components/shared/widgets/VTableAction.vue';
import { isEmpty } from 'lodash';

export default {
    components: {
        VTableAction,
        draggable,
        VTableLocal,
        VFileDragAndDrop,
    },
    props: {
        backendFiles: {
            type: [Array, String],
            default: () => ([]),
        },
        padding: {
            type: Number,
            default: 0,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        mimetype: {
            type: [Array, String],
            default: () => ([]),
        },
        isSingleFile: {
            type: Boolean,
            default: true,
        },
        // PARENT HOOKS
        onAddFile: {
            type: Function,
            default: null,
        },
        onUpdateFile: {
            type: Function,
            default: null,
        },
        onDeleteFile: {
            type: Function,
            default: null,
        },
        onReorderFiles: {
            type: Function,
            default: null,
        },
        // PERMISSIONS
        canAdd: {
            type: Boolean,
            default: false,
        },
        canEdit: {
            type: Boolean,
            default: false,
        },
        canView: {
            type: Boolean,
            default: false,
        },
        canRemove: {
            type: Boolean,
            default: false,
        },
        // OVERWRITE
        actionsDisplayOverWrite: {
            type: Array,
            default: () => (['preview', 'save', 'delete']),
        },
    },
    data() {
        return {
            filesInManager: [],
            isSavable: false,
            newFilesInManager: {
                file: null,
            },
            files: [],
            showTooltipIndex: -1,
            // for img preview
            isPreviewModalVisible: false,
            previewFileUrl: '',
            previewImageWidth: null,
            previewImageHeight: null,
        };
    },
    computed: {
        ...mapGetters([
            'network',
            'component',
            'lang',
            'preventQuitFormular',
        ]),
        rules() {
            return {
                size: 10000,
                mimes: this.mimes,
            };
        },
        isNew() {
            return isEmpty(this.filesInManager);
        },
        networkName() {
            const networkName = `${this.network.name} (${this.network.type.key})`;
            if (!isEmpty(this.component)) {
                return `${networkName} -> ${this.component.name} (${this.component.type.key})`;
            }

            return networkName;
        },
        displayedFiles() {
            // Merge the arrays of backEndFiles and filesInManager
            const allFiles = [...(this.backendFiles || []), ...(this.filesInManager || [])].filter(file => file !== null);
            return allFiles.length > 0 && allFiles[0] ? allFiles.map(file => ({
                ...file,
                size: file.metadata ? `${Math.floor(file.metadata.size / 1000)}KB` : 'N/A',
                // Add other necessary transformations and checks
            })) : [];
        },
        tableHeaders() {
            return [
                'drag_drop',
                'fileName',
                'size',
                'uploadedBy',
                'addedDate',
                'action',
            ].map(value => ({
                text: value === 'drag_drop' ? '' : this.$t(`fileManager.${value}`), // TODO
                value,
            }));
        },
        actions() {
            const actions = [];

            if (this.canView && this.actionsDisplayOverWrite.includes('preview')) {
                actions.push({
                    name: this.$t('fileManager.preview'), // PREVIEW
                    icon: 'mdi-eye',
                });
            }

            if (this.canView && this.actionsDisplayOverWrite.includes('save')) {
                actions.push({
                    name: this.$t('fileManager.save'), // SAVE
                    icon: 'mdi-content-save',
                });
            }

            if (this.canRemove && this.actionsDisplayOverWrite.includes('delete')) {
                actions.push({
                    name: this.$t('fileManager.delete'), // DELETE
                    icon: 'delete',
                    iconAndTextColor: 'red',
                });
            }
            return actions;
        },

    },
    watch: {
        filesInManager: {
            handler() {
                if (this.isNew) {
                    return;
                }
                this.files = this.prepareFile('file');
            },
        },
        isSavable: {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                }
            },
        },
        'filesInManager.file': {
            handler() {
                if (this.isSavable) {
                    this.activePreventQuitFormular();
                } else {
                    this.disablePreventQuitFormular();
                }
            },
        },
    },
    methods: {
        ...mapActions({
            disablePreventQuitFormular: 'disablePreventQuitFormular',
            activePreventQuitFormular: 'activePreventQuitFormular',
        }),

        async addFile(file) {
            if (this.onAddFile) {
                await this.onAddFile(file);
            }
        },
        async updateFile(file) {
            if (this.onUpdateFile) {
                await this.onUpdateFile(file);
            }
        },
        async deleteFile(fileId) {
            if (this.onDeleteFile) {
                await this.onDeleteFile(fileId);
            }
        },
        async reorderFiles(files) {
            if (this.onReorderFiles) {
                await this.onReorderFiles(files);
            }
        },
        async onClickSave() {
            this.isSavable = false;
            if (this.isNew) {
                return this.addFileInManager();
            }

            return this.updateFilesInManager();
        },
        async addFileInManager() {
            const payload = this.preparePayload();
            const { data } = await this.$rq.addFile(null, payload);
            if (this.isSingleFile) {
                // Delete the old file when isSingleFile true
                if (this.backendFiles.length > 0 && this.backendFiles[0] !== null) {
                    await this.deleteFile(this.backendFiles[0].id);
                }
                // Push the new file data to filesInManager
                this.filesInManager = [data];
                this.$emit('change', data);
            }

            this.$toasted.global.toast_success({
                message: this.$t('fileManager.addedFile'),
            });
            await this.resetAll();
        },
        async updateFilesInManager() {
            // TODO only with multiple files

            this.$toasted.global.toast_success({
                message: this.$t('fileManager.updatedFile'),
            });
            await this.resetAll();
        },
        prepareFile(file) {
            if (!this.filesInManager[file]) {
                return null;
            }

            const fileInfo = this.filesInManager[file];
            return new File([], fileInfo.fileName, {
                type: fileInfo.format,
            });
        },
        preparePayload() {
            const formData = new FormData();
            formData.append('file', this.newFilesInManager.file);
            if (!isEmpty(this.component)) { formData.append('component', this.component.id); }
            return formData;
        },
        async onDragChange(e) {
            const { element: file, newIndex } = e.moved;
            this.showTooltipIndex = -1;
            await this.reorderFiles({ file, newIndex });
        },
        // NEW FILE
        onChange(file) {
            this.newFilesInManager.file = file;
            this.isSavable = true;
            this.addFileInManager(file);
        },
        // REMOVE FROM MANAGER
        removeNewFile() {
            this.newFilesInManager.file = null;
            this.isSavable = false;
        },
        async resetAll() {
            this.newFilesInManager.file = null;
            this.isSavable = false;
        },

        // ACTIONS
        async onClickAction(item, action) {
            switch (action.icon) {
                case 'mdi-eye':
                    await this.previewFile(item);
                    break;
                case 'mdi-content-save':
                    await this.saveFile(item);
                    break;
                case 'delete':
                    await this.deleteFile(item.id);
                    break;
                default:
                    break;
            }
        },
        isTooltipActive(currentIndex) {
            return this.showTooltipIndex === currentIndex;
        },
        activateTooltip(currentIndex) {
            this.showTooltipIndex = currentIndex;
            this.customDragItemPosition = currentIndex;
        },

        // PREVIEW
        async previewFile(file) {
            if (this.canView) {
                const { id, mimeType } = file;
                const { data } = await this.$rq.downloadFile(id);

                const blob = new Blob([data], { type: mimeType });
                const url = URL.createObjectURL(blob);

                const img = new Image();
                img.onload = () => {
                    this.previewImageWidth = img.width;
                    this.previewImageHeight = img.height;
                    this.previewFileUrl = url;
                    this.isPreviewModalVisible = true;
                };
                img.src = url;
            }
        },

        closePreview() {
            this.isPreviewModalVisible = false;
            if (this.previewFileUrl) {
                URL.revokeObjectURL(this.previewFileUrl);
                this.previewFileUrl = '';
            }
        },

        // SAVE
        async saveFile(file) {
            if (this.canView) {
                const { id, fileName } = file;
                const { data } = await this.$rq.downloadFile(id);
                this.$utils.saveFileOnDisk(data, fileName);
            }
        },

    },
};
</script>
<style scoped>

.image-preview {
    max-width: 100%;
    max-height: 400px;
    object-fit: contain;
}
.flex-center {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
}
</style>
